import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import Api from "../../../constants/Api";
import colmeiaLogo from "../../../images/logo.png";
import axiosInstance from "../../../services/AxiosInstance";
import { formatFileSize } from "../../../utils/FormatUtils";
import { IMAGE_FRAMING_CENTERED, clampFramingPosition, clampFramingZoom, getImageFramingStyle } from "../../../utils/ImageFramingUtils";
import { optimizeImageForUpload } from "../../../utils/ImageOptimizeUtils";
import { errorAlert, successAlert, warnAlert } from "../alert/defaultAlert";
import HintTooltip from "../Common/HintTooltip";
import ImageFocusEditor from "../Common/ImageFocusEditor";
import SegmentedButtons from "../Forms/Element/SegmentedButtons";
import "./PublicIntroSetting.css";

const STEPS = [1, 2, 3];
const STEP_KEYS = { 1: "One", 2: "Two", 3: "Three" };

// Mesmos limites das colunas no backend (CompanyService valida de novo).
const MAX_TITLE_LENGTH = 90;
const MAX_TEXT_LENGTH = 260;
const MAX_UPLOAD_SIZE = 5 * 1024 * 1024;
// Aceitamos fotos maiores que o limite do backend porque elas são reduzidas antes do envio.
const MAX_SOURCE_SIZE = 25 * 1024 * 1024;
// Abaixo disso, no lado maior, a foto perde nitidez na coluna de meia tela do computador.
const LOW_RESOLUTION_SIDE = 1200;
const IMAGE_ACCEPT = { "image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"], "image/webp": [".webp"] };

/*
 * Proporções aproximadas da foto na página /p/{slug} (PublicPage.css): no computador ela ocupa
 * uma coluna de 46% da largura com a altura da tela (retrato); no celular, uma faixa no topo com
 * até 340 px de altura (paisagem). As molduras do editor e as prévias usam essas proporções.
 */
const DESKTOP_IMAGE_ASPECT = 7 / 8;
const MOBILE_IMAGE_ASPECT = 6 / 5;
const FRAMES = [
    { key: "desktop", label: "Computador", aspectRatio: DESKTOP_IMAGE_ASPECT },
    { key: "mobile", label: "Celular", aspectRatio: MOBILE_IMAGE_ASPECT },
];

const STEP_COUNT_OPTIONS = [
    { value: 0, label: "Nenhum", hint: "Direto ao agendamento" },
    { value: 1, label: "1 card" },
    { value: 2, label: "2 cards" },
    { value: 3, label: "3 cards" },
];

const getImageEndpoint = (step) => `${Api.registration.company_public_page}/images/${step}`;

const hasSavedImage = (company, step) => Boolean(company?.[`hasPublicIntroStep${STEP_KEYS[step]}Image`]);

/** Rascunho editável a partir do que está salvo na empresa. */
const buildDraft = (company) => ({
    stepCount: Math.min(Math.max(Number(company?.publicIntroStepCount) || 0, 0), 3),
    steps: STEPS.map((step) => {
        const key = STEP_KEYS[step];
        return {
            title: company?.[`publicIntroStep${key}Title`] || "",
            text: company?.[`publicIntroStep${key}Text`] || "",
            positionX: clampFramingPosition(company?.[`publicIntroStep${key}ImagePositionX`] ?? 50),
            positionY: clampFramingPosition(company?.[`publicIntroStep${key}ImagePositionY`] ?? 50),
            zoom: clampFramingZoom(company?.[`publicIntroStep${key}ImageZoom`] ?? 100),
        };
    }),
});

const EMPTY_BY_STEP = { 1: null, 2: null, 3: null };
const NONE_REMOVED = { 1: false, 2: false, 3: false };

/**
 * Cards da página pública (/p/{slug}) exibidos antes do agendamento, editados pelo próprio
 * cliente. Textos, quantidade e enquadramento vão num PATCH; cada foto tem upload próprio.
 * Nada é gravado até "Salvar cards": fotos novas e removidas ficam pendentes até lá.
 *
 * @param {object} company - Empresa de /registration/companies/info.
 * @param {function} onSaved - Recarrega a empresa depois de salvar (devolve uma Promise).
 * @param {string} logoSrc - Logo para a prévia (a mesma da seção Logo, inclusive não salva).
 * @param {string} fallbackImageSrc - Banner usado pela página quando o card não tem foto.
 */
const PublicIntroSetting = ({ company, onSaved, logoSrc, fallbackImageSrc }) => {
    const { t } = useTranslation();
    const [draft, setDraft] = useState(() => buildDraft(company));
    const [baseline, setBaseline] = useState(() => buildDraft(company));
    const [activeStep, setActiveStep] = useState(1);
    const [savedImages, setSavedImages] = useState(EMPTY_BY_STEP);
    const [pendingImages, setPendingImages] = useState(EMPTY_BY_STEP);
    const [removedImages, setRemovedImages] = useState(NONE_REMOVED);
    // Dimensões por URL da foto: a mesma foto salva não recarrega ao descartar edições.
    const [imageSizes, setImageSizes] = useState({});
    const [processingStep, setProcessingStep] = useState(null);
    const [saving, setSaving] = useState(false);
    const objectUrlsRef = useRef(new Set());
    const savedImagesRef = useRef(EMPTY_BY_STEP);
    const pendingImagesRef = useRef(EMPTY_BY_STEP);
    pendingImagesRef.current = pendingImages;

    const hasPendingImageChanges = STEPS.some((step) => pendingImages[step] || removedImages[step]);
    const isDirty = hasPendingImageChanges || JSON.stringify(draft) !== JSON.stringify(baseline);
    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;

    // URLs de prévia (blob:) precisam ser liberadas para não acumular memória a cada troca.
    const trackObjectUrl = useCallback((url) => {
        objectUrlsRef.current.add(url);
        return url;
    }, []);

    const releaseObjectUrl = useCallback((url) => {
        if (url && objectUrlsRef.current.has(url)) {
            URL.revokeObjectURL(url);
            objectUrlsRef.current.delete(url);
        }
    }, []);

    useEffect(() => {
        const urls = objectUrlsRef.current;
        return () => {
            urls.forEach((url) => URL.revokeObjectURL(url));
            urls.clear();
        };
    }, []);

    /*
     * A empresa é recarregada também quando a logo ou o banner são salvos. Se houver edição em
     * andamento nos cards, ela não pode ser trocada pelo que veio do servidor.
     */
    useEffect(() => {
        if (!company || isDirtyRef.current) {
            return;
        }
        const fresh = buildDraft(company);
        setDraft(fresh);
        setBaseline(fresh);
    }, [company]);

    const savedImagesKey = STEPS.map((step) => (hasSavedImage(company, step) ? "1" : "0")).join("");

    // Fotos salvas vêm do endpoint autenticado e sem cache, como a logo e o banner.
    useEffect(() => {
        let cancelled = false;
        Promise.all(STEPS.map((step) => {
            if (savedImagesKey[step - 1] !== "1") {
                return Promise.resolve(null);
            }
            return axiosInstance.get(getImageEndpoint(step), { responseType: "blob", params: { t: Date.now() } })
                .then((response) => trackObjectUrl(URL.createObjectURL(response.data)))
                .catch(() => null);
        })).then((urls) => {
            if (cancelled) {
                urls.forEach(releaseObjectUrl);
                return;
            }
            STEPS.forEach((step) => releaseObjectUrl(savedImagesRef.current[step]));
            savedImagesRef.current = { 1: urls[0], 2: urls[1], 3: urls[2] };
            setSavedImages(savedImagesRef.current);
        });
        return () => {
            cancelled = true;
        };
    }, [savedImagesKey, trackObjectUrl, releaseObjectUrl]);

    // Ao reduzir a quantidade, o card em edição precisa continuar entre os visíveis.
    useEffect(() => {
        if (draft.stepCount > 0 && activeStep > draft.stepCount) {
            setActiveStep(draft.stepCount);
        }
    }, [draft.stepCount, activeStep]);

    const updateStep = useCallback((step, changes) => {
        setDraft((previous) => ({
            ...previous,
            steps: previous.steps.map((content, index) => (index === step - 1 ? { ...content, ...changes } : content)),
        }));
    }, []);

    const getStepImageSrc = (step) => pendingImages[step]?.url || (!removedImages[step] ? savedImages[step] : null) || null;

    const handleImageSelected = async (acceptedFiles, rejectedFiles) => {
        const step = activeStep;
        if (rejectedFiles?.length) {
            warnAlert("A foto deve ser PNG, JPG ou WEBP.");
            return;
        }

        const file = acceptedFiles?.[0];
        if (!file) {
            return;
        }

        if (file.size > MAX_SOURCE_SIZE) {
            warnAlert(`A foto deve ter no máximo ${formatFileSize(MAX_SOURCE_SIZE)}.`);
            return;
        }

        setProcessingStep(step);
        try {
            const result = await optimizeImageForUpload(file, { maxBytes: MAX_UPLOAD_SIZE });
            if (result.file.size > MAX_UPLOAD_SIZE) {
                warnAlert("Não foi possível reduzir esta foto para até 5 MB. Escolha outra imagem.");
                return;
            }

            const url = trackObjectUrl(URL.createObjectURL(result.file));
            releaseObjectUrl(pendingImagesRef.current[step]?.url);
            setPendingImages((previous) => ({ ...previous, [step]: { ...result, url } }));
            setRemovedImages((previous) => ({ ...previous, [step]: false }));
            setImageSizes((previous) => ({ ...previous, [url]: { width: result.width, height: result.height } }));
            // Foto nova começa centralizada; o backend faz o mesmo ao receber o arquivo.
            updateStep(step, { ...IMAGE_FRAMING_CENTERED });
        } catch {
            warnAlert("Não foi possível ler esta foto. Tente outra imagem.");
        } finally {
            setProcessingStep(null);
        }
    };

    const currentImageSrc = getStepImageSrc(activeStep);

    // Sem foto, a área inteira abre o seletor; com foto, o clique é do editor e só soltar troca.
    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop: handleImageSelected,
        accept: IMAGE_ACCEPT,
        multiple: false,
        noClick: Boolean(currentImageSrc),
        noKeyboard: Boolean(currentImageSrc),
    });

    // Tirar a foto nova volta para a salva; tirar a salva só vale ao clicar em "Salvar cards".
    const removeImage = (step) => {
        if (pendingImages[step]) {
            releaseObjectUrl(pendingImages[step].url);
            setPendingImages((previous) => ({ ...previous, [step]: null }));
        } else if (hasSavedImage(company, step)) {
            setRemovedImages((previous) => ({ ...previous, [step]: true }));
        }
        updateStep(step, { ...IMAGE_FRAMING_CENTERED });
    };

    const discardChanges = () => {
        STEPS.forEach((step) => releaseObjectUrl(pendingImages[step]?.url));
        setPendingImages(EMPTY_BY_STEP);
        setRemovedImages(NONE_REMOVED);
        setDraft(baseline);
    };

    const save = async () => {
        setSaving(true);
        try {
            // Ordem importa: o upload recentraliza o enquadramento, então o PATCH vem por último.
            for (const step of STEPS) {
                if (removedImages[step] && !pendingImages[step] && hasSavedImage(company, step)) {
                    await axiosInstance.delete(getImageEndpoint(step));
                }
            }

            for (const step of STEPS) {
                if (pendingImages[step]) {
                    const formData = new FormData();
                    formData.append("file", pendingImages[step].file);
                    await axiosInstance.post(getImageEndpoint(step), formData, {
                        headers: { "content-type": "multipart/form-data" },
                    });
                }
            }

            await axiosInstance.patch(Api.registration.company_public_page, {
                stepCount: draft.stepCount,
                // Os três cards vão juntos para reduzir a quantidade não apagar o conteúdo dos demais.
                steps: draft.steps.map((content) => ({
                    title: content.title,
                    text: content.text,
                    imagePositionX: content.positionX,
                    imagePositionY: content.positionY,
                    imageZoom: content.zoom,
                })),
            });

            /*
             * A foto enviada passa a ser a salva sem baixar de novo: é o mesmo arquivo que foi para
             * o servidor. Assim a prévia não pisca a foto antiga enquanto a empresa recarrega.
             */
            const nextSaved = { ...savedImagesRef.current };
            STEPS.forEach((step) => {
                if (pendingImages[step] || removedImages[step]) {
                    releaseObjectUrl(nextSaved[step]);
                    nextSaved[step] = pendingImages[step]?.url || null;
                }
            });
            savedImagesRef.current = nextSaved;
            setSavedImages(nextSaved);
            setPendingImages(EMPTY_BY_STEP);
            setRemovedImages(NONE_REMOVED);
            setBaseline(draft);
            isDirtyRef.current = false;
            await onSaved?.();
            successAlert(t("message.data_updated_successfully"));
        } catch (error) {
            errorAlert(error);
        } finally {
            setSaving(false);
        }
    };

    const publicPageUrl = company?.publicSlug ? `${window.location.origin}/p/${company.publicSlug}` : null;
    const activeContent = draft.steps[activeStep - 1];
    const activeImageSize = currentImageSrc ? imageSizes[currentImageSrc] : null;
    const activePendingImage = pendingImages[activeStep];
    const isLowResolution = activeImageSize && Math.max(activeImageSize.width, activeImageSize.height) < LOW_RESOLUTION_SIDE;

    const stepTabs = useMemo(() => STEPS.filter((step) => step <= draft.stepCount).map((step) => ({
        value: step,
        label: `Card ${step}`,
        icon: (pendingImages[step] || (!removedImages[step] && hasSavedImage(company, step))) ? "fa-solid fa-image" : "fa-regular fa-image",
        title: draft.steps[step - 1].title || `Card ${step}`,
    })), [draft, pendingImages, removedImages, company]);

    const statusLabel = isDirty
        ? "Alterações não salvas"
        : baseline.stepCount > 0 ? `${baseline.stepCount} ${baseline.stepCount === 1 ? "card ativo" : "cards ativos"}` : "Sem apresentação";

    return (
        <section className="branding-section branding-section--intro public-intro-setting">
            <div className="branding-section__heading">
                <span className="branding-section__icon"><i className="fa-solid fa-window-maximize" aria-hidden="true" /></span>
                <div>
                    <h5 className="mb-1">Página de apresentação</h5>
                    <p className="text-muted mb-0">
                        Até três cards que o cliente vê no link público antes de agendar.
                        {publicPageUrl && (
                            <>
                                {" "}
                                <a href={publicPageUrl} target="_blank" rel="noopener noreferrer" className="public-intro-setting__link">
                                    Abrir página <i className="fa-solid fa-arrow-up-right-from-square ms-1" aria-hidden="true" />
                                </a>
                            </>
                        )}
                    </p>
                </div>
                <span className={`branding-asset-status${isDirty ? " is-pending" : baseline.stepCount > 0 ? " is-saved" : " is-empty"}`}>
                    {statusLabel}
                </span>
            </div>

            <div className="public-intro-setting__count">
                <span className="form-label">
                    Quantidade de cards <HintTooltip text="Com Nenhum, o link público abre direto no agendamento. Reduzir a quantidade não apaga os cards que saírem: eles voltam se você aumentar de novo." />
                </span>
                <SegmentedButtons
                    options={STEP_COUNT_OPTIONS}
                    value={draft.stepCount}
                    onChange={(value) => setDraft((previous) => ({ ...previous, stepCount: value }))}
                    ariaLabel="Quantidade de cards"
                />
            </div>

            {draft.stepCount === 0 ? (
                <div className="public-intro-setting__empty">
                    <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                    <span>O link público vai direto para o agendamento. Escolha 1 a 3 cards para apresentar a empresa antes.</span>
                </div>
            ) : (
                <>
                    {draft.stepCount > 1 && (
                        <SegmentedButtons
                            className="public-intro-setting__tabs"
                            options={stepTabs}
                            value={activeStep}
                            onChange={setActiveStep}
                            ariaLabel="Card em edição"
                            compact
                        />
                    )}

                    <div className="public-intro-setting__editor">
                        <div className="public-intro-setting__texts">
                            <div className="branding-field">
                                <label htmlFor={`publicIntroTitle${activeStep}`} className="form-label">
                                    Título do card {activeStep}
                                </label>
                                <input
                                    id={`publicIntroTitle${activeStep}`}
                                    type="text"
                                    className="form-control"
                                    placeholder={activeStep === 1 ? "Ex.: Bem-vindo! Que bom ter você aqui" : "Ex.: Agende em poucos passos"}
                                    value={activeContent.title}
                                    maxLength={MAX_TITLE_LENGTH}
                                    onChange={(event) => updateStep(activeStep, { title: event.target.value.slice(0, MAX_TITLE_LENGTH) })}
                                />
                                <small className="public-intro-setting__counter">{activeContent.title.length}/{MAX_TITLE_LENGTH}</small>
                            </div>
                            <div className="branding-field">
                                <label htmlFor={`publicIntroText${activeStep}`} className="form-label">
                                    Texto do card {activeStep}
                                </label>
                                <textarea
                                    id={`publicIntroText${activeStep}`}
                                    className="form-control"
                                    rows={4}
                                    placeholder="Conte o que o cliente vai encontrar: serviços, diferenciais, como funciona o agendamento."
                                    value={activeContent.text}
                                    maxLength={MAX_TEXT_LENGTH}
                                    onChange={(event) => updateStep(activeStep, { text: event.target.value.slice(0, MAX_TEXT_LENGTH) })}
                                />
                                <small className="public-intro-setting__counter">{activeContent.text.length}/{MAX_TEXT_LENGTH}</small>
                            </div>
                            <small className="text-muted d-block">Em branco, a página usa um título e um texto padrão.</small>
                        </div>

                        <div className="public-intro-setting__image">
                            <div className="public-intro-setting__image-heading">
                                <span className="form-label mb-0">
                                    Foto do card {activeStep} <HintTooltip text="PNG, JPG ou WEBP. Fotos grandes do celular são reduzidas automaticamente antes do envio. Recomendado: 1600 px ou mais no lado maior." />
                                </span>
                                {activeImageSize && (
                                    <span className={`public-intro-setting__chip${isLowResolution ? " is-warning" : ""}`}>
                                        {activeImageSize.width} × {activeImageSize.height} px
                                        {isLowResolution ? ", resolução baixa" : ""}
                                    </span>
                                )}
                            </div>

                            <div
                                {...getRootProps({
                                    className: `public-intro-setting__dropzone${currentImageSrc ? " has-image" : ""}${isDragActive ? " is-drag-active" : ""}`,
                                })}
                            >
                                <input {...getInputProps()} />
                                {processingStep === activeStep ? (
                                    <div className="public-intro-setting__dropzone-empty" role="status">
                                        <Spinner animation="border" size="sm" />
                                        <span>Preparando a foto…</span>
                                    </div>
                                ) : currentImageSrc ? (
                                    <ImageFocusEditor
                                        key={currentImageSrc}
                                        src={currentImageSrc}
                                        alt={`Foto do card ${activeStep}`}
                                        framing={activeContent}
                                        frames={FRAMES}
                                        onChange={(framing) => updateStep(activeStep, framing)}
                                        onImageLoad={(size) => setImageSizes((previous) => ({ ...previous, [currentImageSrc]: size }))}
                                    />
                                ) : (
                                    <div className="public-intro-setting__dropzone-empty">
                                        <i className="fa-regular fa-image fa-2x" aria-hidden="true" />
                                        <strong>{isDragActive ? "Solte a foto aqui" : "Arraste uma foto ou clique para escolher"}</strong>
                                        <span>
                                            {fallbackImageSrc
                                                ? "Sem foto, este card mostra o banner do agendamento."
                                                : "Sem foto, este card mostra a inicial da empresa sobre um fundo neutro."}
                                        </span>
                                    </div>
                                )}
                                {isDragActive && currentImageSrc && (
                                    <div className="public-intro-setting__drop-overlay">Solte para trocar a foto</div>
                                )}
                            </div>

                            {currentImageSrc && (
                                <p className="public-intro-setting__help">
                                    Toque ou arraste o círculo até o que não pode ser cortado. As molduras mostram o que aparece no computador e no celular.
                                </p>
                            )}

                            {activePendingImage?.optimized && (
                                <p className="public-intro-setting__help">
                                    <i className="fa-solid fa-wand-magic-sparkles me-1" aria-hidden="true" />
                                    Foto otimizada para a web: {formatFileSize(activePendingImage.originalSize)} para {formatFileSize(activePendingImage.file.size)}.
                                </p>
                            )}

                            {currentImageSrc && (
                                <div className="branding-action-row d-flex gap-2">
                                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={open} disabled={saving}>
                                        <i className="fa-solid fa-arrows-rotate me-2" aria-hidden="true" />
                                        Trocar foto
                                    </button>
                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeImage(activeStep)} disabled={saving}>
                                        <i className="fa-solid fa-trash me-2" aria-hidden="true" />
                                        Remover foto
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <PublicIntroPreview
                        companyName={company?.name}
                        logoSrc={logoSrc}
                        imageSrc={currentImageSrc}
                        fallbackImageSrc={fallbackImageSrc}
                        framing={currentImageSrc ? activeContent : IMAGE_FRAMING_CENTERED}
                        title={activeContent.title}
                        text={activeContent.text}
                        step={activeStep}
                        stepCount={draft.stepCount}
                    />
                </>
            )}

            <div className="branding-action-row d-flex gap-2 mt-3">
                <button type="button" className="btn btn-primary" onClick={save} disabled={saving || !isDirty || processingStep !== null}>
                    {saving ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="fa-solid fa-floppy-disk me-2" aria-hidden="true" />}
                    Salvar cards
                </button>
                {isDirty && (
                    <button type="button" className="btn btn-outline-secondary" onClick={discardChanges} disabled={saving}>
                        Descartar
                    </button>
                )}
            </div>
        </section>
    );
};

/**
 * Miniatura da página /p/{slug} no computador e no celular, com o mesmo layout, sombreado e
 * enquadramento da página real, para o cliente ver o card antes de salvar.
 */
const PublicIntroPreview = ({ companyName, logoSrc, imageSrc, fallbackImageSrc, framing, title, text, step, stepCount }) => {
    const src = imageSrc || fallbackImageSrc;
    const initials = (companyName || "C").trim().slice(0, 1).toUpperCase();
    const isLastStep = step >= stepCount;

    const media = (
        <div className="public-intro-preview__media">
            {src
                ? <img src={src} alt="" style={getImageFramingStyle(framing)} />
                : <div className="public-intro-preview__placeholder">{initials}</div>}
        </div>
    );

    const content = (
        <div className="public-intro-preview__content">
            <div className="public-intro-preview__brand">
                {/* Sem logo da empresa, a página pública usa a da Colmeia. */}
                <img src={logoSrc || colmeiaLogo} alt="" />
                <span>{companyName}</span>
            </div>
            <div className="public-intro-preview__count">{step} de {stepCount}</div>
            <div className={`public-intro-preview__title${title ? "" : " is-default"}`}>{title || "Título padrão"}</div>
            <div className={`public-intro-preview__text${text ? "" : " is-default"}`}>{text || "Texto padrão da página."}</div>
            <div className="public-intro-preview__button">{isLastStep ? "Agendar agora" : "Próximo"} -&gt;</div>
            <div className="public-intro-preview__dots" aria-hidden="true">
                {Array.from({ length: stepCount }, (_, index) => (
                    <span key={index} className={index === step - 1 ? "is-active" : ""} />
                ))}
            </div>
        </div>
    );

    return (
        <div className="public-intro-preview" aria-label="Prévia do card na página pública">
            <div className="public-intro-preview__heading">
                <h6 className="mb-1">Prévia do card {step}</h6>
                <p className="text-muted mb-0">Como o cliente vê o card antes de agendar. Salve para publicar.</p>
            </div>
            <div className="public-intro-preview__devices">
                <figure className="public-intro-preview__device public-intro-preview__device--desktop">
                    <div className="public-intro-preview__screen">
                        {media}
                        {content}
                    </div>
                    <figcaption><i className="fa-solid fa-desktop me-1" aria-hidden="true" />Computador</figcaption>
                </figure>
                <figure className="public-intro-preview__device public-intro-preview__device--mobile">
                    <div className="public-intro-preview__screen">
                        {media}
                        {content}
                    </div>
                    <figcaption><i className="fa-solid fa-mobile-screen me-1" aria-hidden="true" />Celular</figcaption>
                </figure>
            </div>
        </div>
    );
};

export default PublicIntroSetting;
